/**
 * Date utility functions for timezone-safe date handling
 * All dates are stored as YYYY-MM-DD strings and handled in local timezone
 */

/**
 * Get today's date in YYYY-MM-DD format (local timezone)
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getTodayDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a date string (YYYY-MM-DD) into a Date object in local timezone
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Date} Date object in local timezone
 */
export const parseDateString = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Format a date string for display
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateString = (dateString, options = {}) => {
  const date = parseDateString(dateString);
  const defaultOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

/**
 * Convert a Date object to YYYY-MM-DD string
 * @param {Date} date - Date object
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const dateToString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Add days to a date string
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {number} days - Number of days to add (can be negative)
 * @returns {string} New date string in YYYY-MM-DD format
 */
export const addDays = (dateString, days) => {
  const date = parseDateString(dateString);
  date.setDate(date.getDate() + days);
  return dateToString(date);
};

/**
 * Get the previous date
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Previous date in YYYY-MM-DD format
 */
export const getPreviousDate = (dateString) => {
  return addDays(dateString, -1);
};

/**
 * Get the next date
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Next date in YYYY-MM-DD format
 */
export const getNextDate = (dateString) => {
  return addDays(dateString, 1);
};

/**
 * Check if two date strings are the same
 * @param {string} date1 - First date in YYYY-MM-DD format
 * @param {string} date2 - Second date in YYYY-MM-DD format
 * @returns {boolean} True if dates are the same
 */
export const isSameDate = (date1, date2) => {
  return date1 === date2;
};

/**
 * Check if a date string is today
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean} True if date is today
 */
export const isToday = (dateString) => {
  return dateString === getTodayDateString();
};

/**
 * Get the first day of the month (0 = Sunday, 6 = Saturday)
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {number} Day of week (0-6)
 */
export const getFirstDayOfMonth = (year, month) => {
  return new Date(year, month - 1, 1).getDay();
};

/**
 * Get the number of days in a month
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {number} Number of days in the month
 */
export const getDaysInMonth = (year, month) => {
  return new Date(year, month, 0).getDate();
};

/**
 * Get date components from a date string
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {object} Object with year, month, day properties
 */
export const getDateComponents = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return { year, month, day };
};

/**
 * Create a date string from components
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @param {number} day - Day
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const createDateString = (year, month, day) => {
  const m = String(month).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
};
